// R7 — frontend error catalog.
//
// The single source of truth for turning a typed wire ErrorCode into a warm,
// safe, user-facing message. The backend (internal/domain/errormap.go) already
// computes a safe message and ships it on the wire, but the frontend owns the
// FINAL copy so we can tune tone, localize, and guarantee a graceful fallback
// for codes an older bundle doesn't know about.
//
// Tone (Gate-0): first-person "we", soften failures, never expose internals,
// always pair a retryable error with a "Try again" affordance (the `retryable`
// hint flows through here so callers can render that affordance consistently).
//
// Localized (R8 Phase 1b): friendlyMessage resolves the copy through the
// errors.* next-intl namespace via the imperative bridge (errorI18n.ts) —
// catalog entries below remain the English source of truth (their literals
// are byte-identical to messages/en.json `errors.*`) AND the pre-hydration /
// missing-key fallback. Retryability stays here: it's behavior, not copy.

import { ErrorCode } from '@/lib/protobuf/gen/errors/errors_pb';
import { translateError } from '@/lib/errors/errorI18n';
import { formatDuration } from '@/lib/errors/formatDuration';
import { imperativeLocale } from '@/lib/i18n/imperative';
import { isDemoReadOnlyRefusal } from '@/lib/auth/demoGuest';
import { isLimitUnit, type LimitUnit } from '@/lib/limits/types';

export interface CatalogEntry {
  /** Warm, safe, user-facing message. Never contains internal detail. */
  message: string;
  /** Whether the UI should offer a consistent "Try again" affordance. */
  retryable: boolean;
}

/**
 * WireError is the minimal shape every error surface (WS envelope.errorEvent,
 * legacy envelope.error, HTTP JSON body) normalises to before reaching the UI.
 * `code` is the typed ErrorCode; `message` is the safe server message (used only
 * as a fallback for unknown codes); `retryable` overrides the catalog's hint
 * when the server knows better (e.g. session-locked is retryable after unlock).
 */
export interface WireError {
  code?: ErrorCode | number;
  message?: string;
  retryable?: boolean;
  /**
   * The RAW machine code string as the server spelled it (`"ERROR_CODE_…"`,
   * or a bare snake_case code like `"demo_read_only"`), when one was present.
   *
   * `code` above only survives codes this bundle's ErrorCode enum knows; a
   * string code the enum has no member for would otherwise be discarded during
   * normalization. Surfaces that branch on a specific server-defined code
   * (currently the `/demo` guest refusal) read this instead.
   */
  codeName?: string;
  /**
   * BILL-20a: the capability id a PLAN_UPGRADE_REQUIRED refusal is about
   * ("workspaces", "docling_premium", …) — HTTP body `feature` / ErrorEvent
   * field 6. Absent when the server sent "" (never set to "").
   */
  feature?: string;
  /**
   * BILL-20a: the lowest tier that grants `feature`, when the server knows it
   * — HTTP `required_tier` / ErrorEvent field 7. Absent ⇒ PlanUpsell reads
   * capabilities[feature].required_tier (design KD-S3).
   */
  requiredTier?: string;
  /**
   * The enforced value of the limit this refusal hit, for every limit code
   * (BILL-10, CHAT-71, LIMIT-03) — ErrorEvent field 8 or the HTTP body's
   * `limit`. Absent when the server sent 0 (never set to 0). friendlyMessage
   * names the number with it; without it the catalog literal is shown.
   */
  limit?: number;
  /** LIMIT-03: what `limit` and `current` count — ErrorEvent field 10 / HTTP `limit_unit`. */
  limitUnit?: LimitUnitName;
  /** LIMIT-03: what the user had or sent — ErrorEvent field 11 / HTTP `current`. Absent = not measured. */
  current?: number;
  /** CHAT-71 / LIMIT-07: seconds until a RATE_LIMITED retry can pass — ErrorEvent field 9, HTTP `retry_after_seconds`, else the `Retry-After` header. Absent = not known. */
  retryAfterSeconds?: number;
  /** LIMIT-07: the window a rate limit counts over, in seconds — ErrorEvent field 13 / HTTP `window_seconds`. Absent = not known. */
  windowSeconds?: number;
  /**
   * WF-38: the input a VALIDATION refusal is about — ErrorEvent field 3, or the
   * legacy carrier's `details.field`. Absent when the server sent "".
   */
  field?: string;
  /**
   * WF-38: the named problems of a VALIDATION refusal — ErrorEvent field 12.
   * The workflow editors render each at its path through issueText. Absent when
   * the server sent none.
   */
  issues?: WireIssue[];
}

/**
 * WireIssue is one named problem in a submitted spec (WF-38), copied out of the
 * proto ValidationIssue into a plain object. `code` is the stable key the
 * frontend localises by (`workflows.workflowIssue.<code>`); `message` is the
 * server's English text, shown for a code this bundle has no copy for.
 */
export interface WireIssue {
  /** The offending field, e.g. "triggers[0].schedule" or "steps[draft].retry". */
  path: string;
  code: string;
  /** The numbers the check enforced ("floor", "interval", "limit", "fields"). */
  params: Record<string, string>;
  message: string;
  /** "error" | "warning". */
  severity: string;
}

/**
 * isSubscriptionRequired reports whether a normalised wire error is the
 * server's BILL-03 refusal for a caller without a subscription — HTTP 402
 * `error_code: "ERROR_CODE_SUBSCRIPTION_REQUIRED"` or WS `ErrorEvent.code`
 * 240. Lives in this leaf module (not the entitlement store) so toastStore can
 * test for it without importing the store's auth dependency graph.
 */
export function isSubscriptionRequired(wire: WireError | null | undefined): boolean {
  return (
    wire?.code === ErrorCode.SUBSCRIPTION_REQUIRED ||
    wire?.codeName === 'ERROR_CODE_SUBSCRIPTION_REQUIRED'
  );
}

/**
 * isPlanUpgradeRequired reports whether a normalised wire error is BILL-20a's
 * refusal for a feature the caller's plan does not include — HTTP 403
 * `error_code: "ERROR_CODE_PLAN_UPGRADE_REQUIRED"` or WS `ErrorEvent.code` 241.
 * The UI renders PlanUpsell for it, never the FORBIDDEN line. Leaf module for
 * the same reason as isSubscriptionRequired (toastStore and lib/http import it).
 */
export function isPlanUpgradeRequired(wire: WireError | null | undefined): boolean {
  return (
    wire?.code === ErrorCode.PLAN_UPGRADE_REQUIRED ||
    wire?.codeName === 'ERROR_CODE_PLAN_UPGRADE_REQUIRED'
  );
}

/**
 * isImageWaiverRequired reports whether a normalised wire error is DECK-13's
 * refusal for image generation at a Sovereign-or-stricter floor with no waiver
 * in force — WS `ErrorEvent.code` 204 or HTTP `error_code:
 * "ERROR_CODE_IMAGE_GEN_SOVEREIGN_CONSENT_REQUIRED"`. The codeName leg keeps an
 * older bundle that does not know 204 on the divert. toastStore routes it to the
 * Privacy waiver control.
 */
export function isImageWaiverRequired(wire: WireError | null | undefined): boolean {
  return (
    wire?.code === ErrorCode.IMAGE_GEN_SOVEREIGN_CONSENT_REQUIRED ||
    wire?.codeName === 'ERROR_CODE_IMAGE_GEN_SOVEREIGN_CONSENT_REQUIRED'
  );
}

/**
 * GENERIC_FALLBACK is shown for any code the catalog doesn't recognise
 * (forward-compat: a newer backend can introduce a code an older frontend
 * bundle hasn't shipped a string for). Warm + retryable so the user always
 * gets a sensible, non-scary affordance.
 */
export const GENERIC_FALLBACK: CatalogEntry = {
  message: 'Sorry — something went wrong. Please try again.',
  retryable: true,
};

/**
 * The `/demo` guest refusal, which is an invitation rather than a failure.
 *
 * It has no ErrorCode enum member (the server sends a bare `demo_read_only`
 * string), so it lives outside CATALOG and is matched by code name / message.
 * `toastStore.showError` diverts it to a registration prompt before rendering;
 * this entry is the copy for the surfaces that render `err.message` inline and
 * never reach the toast path — without it a guest would see the raw wire token
 * `demo_read_only`.
 *
 * Copy note (MKT-03 precedent): the demo account is not a free tier and the
 * product has none, so this must not promise a "free account".
 */
export const DEMO_READ_ONLY_ENTRY: CatalogEntry = {
  message: 'This demo is read-only. Create an account to do this in your own workspace.',
  retryable: false,
};

// The catalog. Mirrors internal/domain/errormap.go::codeProps — keep the copy
// and the retryable hints in sync (the backend message is the safe default; the
// frontend copy is what the user actually sees).
const CATALOG: Partial<Record<ErrorCode, CatalogEntry>> = {
  // ---- Core ---------------------------------------------------------------
  [ErrorCode.NOT_FOUND]: {
    message: "We couldn't find that — it may have been removed.",
    retryable: false,
  },
  [ErrorCode.CONFLICT]: {
    message: 'That clashes with something already in progress. Please refresh and try again.',
    retryable: true,
  },
  [ErrorCode.VALIDATION]: {
    message: "Hmm, something about that wasn't quite right. Please check it and try again.",
    retryable: false,
  },
  [ErrorCode.UNAUTHORIZED]: {
    message: 'Please sign in to continue.',
    retryable: false,
  },
  [ErrorCode.FORBIDDEN]: {
    message: "Sorry — you don't have access to that.",
    retryable: false,
  },
  [ErrorCode.BUDGET_EXHAUSTED]: {
    message: "You've used all your credits for this.",
    retryable: false,
  },
  [ErrorCode.HITL_REQUIRED]: {
    message: 'This needs your approval before we can continue.',
    retryable: false,
  },
  [ErrorCode.RATE_LIMITED]: {
    message: 'Just a sec — that was a bit too fast. Please try again in a moment.',
    retryable: true,
  },
  [ErrorCode.UNAVAILABLE]: {
    message: "We're having a brief hiccup. Please try again in a moment.",
    retryable: true,
  },
  [ErrorCode.INTERNAL]: {
    message: 'Sorry — something went wrong on our end. Please try again.',
    retryable: true,
  },
  [ErrorCode.TIMEOUT]: {
    message: 'That took longer than expected. Please try again.',
    retryable: true,
  },
  [ErrorCode.PAYLOAD_TOO_LARGE]: {
    message: 'That file is a bit too large. Please try a smaller one.',
    retryable: false,
  },
  [ErrorCode.UNSUPPORTED_MEDIA]: {
    message: "Sorry — we can't work with that file type.",
    retryable: false,
  },

  // ---- Recordings & ASR ---------------------------------------------------
  [ErrorCode.RECORDING_SESSION_LOCKED]: {
    message: 'Your session is locked — please unlock it, then try again.',
    retryable: true, // after unlock
  },
  [ErrorCode.RECORDING_NOT_READY]: {
    message: 'This recording is still being transcribed — please give it a moment.',
    retryable: true,
  },
  // A cancel/retry that lost a race with the job itself. Deliberately NOT
  // alarming and deliberately NOT actionable: nothing went wrong, the user took
  // no wrong action, and the state they wanted is the state they already have —
  // so no "refresh", no "try again", and `retryable: false` (re-attempting is
  // pointless, and a longer toast dwell would only draw the eye to a non-event).
  // The global WS error handler swallows this class entirely
  // (features/execution/executionStore.ts); this entry is the copy for the
  // surfaces that render `err.message` inline.
  [ErrorCode.RECORDING_TRANSITION_REFUSED]: {
    message: 'That transcription already moved on — nothing left to do.',
    retryable: false,
  },
  [ErrorCode.RECORDING_UPLOAD_FAILED]: {
    message: "Sorry — we couldn't process that recording just now. Please try again.",
    retryable: true,
  },

  // ---- Notes & note assets -------------------------------------------------
  [ErrorCode.NOTE_SESSION_LOCKED]: {
    message: 'Your session is locked — please unlock it, then try again.',
    retryable: true, // after unlock
  },
  [ErrorCode.NOTE_ASSET_QUOTA_EXCEEDED]: {
    message: 'Image storage limit reached — delete some attachments first.',
    retryable: false,
  },

  // ---- Chat -----------------------------------------------------------------
  [ErrorCode.CHAT_SESSION_LIMIT]: {
    message: "You've reached the chat session limit — delete an old conversation to start a new one.",
    retryable: false,
  },
  [ErrorCode.CHAT_MESSAGE_LIMIT]: {
    message: 'This conversation has reached its message limit — start a new chat to continue.',
    retryable: false,
  },
  [ErrorCode.CHAT_SESSION_LOCKED]: {
    message: 'Your session is locked — please unlock it, then try again.',
    retryable: true, // after unlock
  },
  [ErrorCode.CHAT_IMAGE_GEN_SOVEREIGN_UNAVAILABLE]: {
    message: "Image generation isn't available in Confidential mode or in automated runs — it uses a non-sovereign model.",
    retryable: false,
  },
  // DECK-13: the waiver is missing at a Sovereign-or-stricter saved floor. The
  // toast routes to the Privacy waiver control (toastStore), with no retry.
  [ErrorCode.IMAGE_GEN_SOVEREIGN_CONSENT_REQUIRED]: {
    message: 'Image generation sends your prompt outside the sovereign boundary — allow it in Settings → Privacy first.',
    retryable: false,
  },

  // ---- Docling premium conversion ----------------------------------------
  [ErrorCode.DOCLING_QUOTA_EXCEEDED]: {
    message: "You've used all your high-effort conversions for this month.",
    retryable: false,
  },
  [ErrorCode.DOCLING_RETRY_NOT_AVAILABLE]: {
    message: "Sorry — a free retry isn't available for this document.",
    retryable: false,
  },

  // ---- Engine / store ownership ------------------------------------------
  [ErrorCode.STORE_OWNER_MISMATCH]: {
    message: "We couldn't find that research — it may have been removed.",
    retryable: false,
  },

  // ---- Billing ------------------------------------------------------------
  // Normally never read: toastStore diverts this code to the paywall before
  // rendering. It is the copy for surfaces that render `err.message` inline.
  [ErrorCode.SUBSCRIPTION_REQUIRED]: {
    message: 'This needs an active plan. Choose a plan to continue.',
    retryable: false,
  },
  // BILL-20a: the plan does not include the feature. Normally never read as a
  // line — apiFetch's 241 handler and toastStore.showError render PlanUpsell
  // instead. It is the copy for surfaces that render `err.message` inline, and
  // it mirrors domain.CodePlanUpgradeRequired's SafeMessage.
  [ErrorCode.PLAN_UPGRADE_REQUIRED]: {
    message: 'This is part of a higher plan.',
    retryable: false,
  },

  // ---- Organisation security policy (MERIDIAN-66) ------------------------
  // A policy decision, not a failure: the excludes-all copy points at the
  // person who can change it, and only the unreadable case is worth retrying.
  [ErrorCode.ORG_POLICY_EXCLUDES_ALL]: {
    message: "Your organisation's security policy allows no model for this request — ask an org admin.",
    retryable: false,
  },
  [ErrorCode.ORG_POLICY_UNAVAILABLE]: {
    message: "Your organisation's security policy can't be read right now — try again shortly.",
    retryable: true,
  },

  // ---- Encryption keys (AUTH-15) -----------------------------------------
  // /crypto/recover refused the master-KEK proof. Not retryable as is: an old
  // bundle that sends no proof needs a reload, and a stale sheet needs a new one.
  [ErrorCode.MASTER_KEK_PROOF_FAILED]: {
    message: 'We could not confirm your recovery against your current key. Reload the page and try again. If it still fails, your recovery sheet may be out of date.',
    retryable: false,
  },

  // ---- Research concurrency (BILL-10) -------------------------------------
  // The plan's cap on research runs at once. Retryable: a slot frees when a
  // run finishes or is stopped. With the limit on the wire, friendlyMessage
  // uses errors.research.concurrencyLimit (names the number) instead.
  [ErrorCode.RESEARCH_CONCURRENCY_LIMIT]: {
    message:
      'You already have the most research your plan runs at once. Wait for one to finish, or stop one, and try again.',
    retryable: true,
  },

  // ---- AI gateway concurrency (LIMIT-03) ------------------------------------
  [ErrorCode.AI_CONCURRENCY_LIMIT]: {
    message: 'The AI service is busy for your workspace right now. Wait a moment and try again.',
    retryable: true,
  },

  // ---- Search providers --------------------------------------------------
  [ErrorCode.SERPER_CAP_EXCEEDED]: {
    message: 'We’ve gathered as much as we can from search for now.',
    retryable: false,
  },
};

/**
 * lookup returns the catalog entry for a code, falling back to GENERIC_FALLBACK
 * for unknown / unspecified codes. Never throws.
 */
export function lookup(code: ErrorCode | number | undefined | null): CatalogEntry {
  if (code == null || code === ErrorCode.UNSPECIFIED) return GENERIC_FALLBACK;
  return CATALOG[code as ErrorCode] ?? GENERIC_FALLBACK;
}

/**
 * DETAIL_PREFERRING codes carry user-actionable specifics in the server
 * message that the code class cannot reconstruct — uniqueness conflicts
 * ("email already registered") and validation detail. For these, a non-empty
 * server message WINS over the (localized) catalog copy; the detail stays
 * English until it graduates to a granular code (the parent design's
 * "English detail fallback" bucket). Everything else keeps R7 semantics:
 * catalog copy wins so tone stays consistent — and localizes.
 */
const DETAIL_PREFERRING: ReadonlySet<ErrorCode> = new Set([
  ErrorCode.VALIDATION,
  ErrorCode.CONFLICT,
]);

/**
 * LimitUnitName is a limit refusal's unit, as the server names it (LIMIT-03).
 * It is the same closed set as LIMIT-05's `LimitUnit` (`@/lib/limits/types`):
 * both are domain.LimitUnit.String() on the Go side, so one type serves both.
 */
export type LimitUnitName = LimitUnit;
export function isLimitUnitName(v: unknown): v is LimitUnitName {
  return isLimitUnit(v);
}

/**
 * limitValues builds friendlyMessage's ICU values: the limit, unit and current
 * (LIMIT-03), the window and the wait as localized durations (LIMIT-07).
 * undefined when neither a limit nor a wait is known. CHAT-72 consumes this
 * render (packet D57).
 */
export function limitValues(
  wire: Pick<WireError, 'limit' | 'limitUnit' | 'current' | 'retryAfterSeconds' | 'windowSeconds'>,
): Record<string, string | number> | undefined {
  const hasLimit = wire.limit != null && wire.limit > 0;
  const hasWait = wire.retryAfterSeconds != null && wire.retryAfterSeconds > 0;
  if (!hasLimit && !hasWait) return undefined;
  const locale = imperativeLocale();
  const v: Record<string, string | number> = {};
  if (hasLimit) {
    v.limit = wire.limit as number;
    if (wire.limitUnit) v.unit = wire.limitUnit;
    if (wire.current != null && wire.current > 0) v.current = wire.current;
    if (wire.windowSeconds != null && wire.windowSeconds > 0) v.window = formatDuration(wire.windowSeconds, locale, 'window');
  }
  if (hasWait) v.retryAfter = formatDuration(wire.retryAfterSeconds as number, locale, 'wait');
  return v;
}

/**
 * RATE_LIMITED and PAYLOAD_TOO_LARGE prefer the server detail only when that
 * detail names a number — the concrete wait or the limit. BILL-06's re-run
 * limit says "(30 per hour); try again in 42 min", and the catalog's "try again
 * in a moment" understates a wait of up to an hour, which invites the repeated
 * clicks the limit refuses. The Workspace export's 413 says "larger than the
 * 32 MB limit … ask your operator to raise the limit" (MEM-09m), where the
 * catalog's "try a smaller one" names neither the limit nor the way out
 * (CLAUDE.md Limits rule). A detail with no number in it ("Too Many Requests"
 * from a generic limiter) carries nothing the catalog copy lacks, so the warm,
 * localized copy keeps it.
 */
function prefersLimitDetail(code: ErrorCode | number, detail: string): boolean {
  return (code === ErrorCode.RATE_LIMITED || code === ErrorCode.PAYLOAD_TOO_LARGE) && /\d/.test(detail);
}

/**
 * codeName resolves the enum member name ("NOT_FOUND") for a numeric code —
 * the errors.* catalog key (stable and greppable, survives renumbering).
 */
function codeName(code: ErrorCode | number): string | undefined {
  const name = (ErrorCode as Record<number, unknown>)[code as number];
  return typeof name === 'string' ? name : undefined;
}

/**
 * limitMessage picks the most specific row whose placeholders are all present:
 * `errors.limit.<CODE>` first (keeps that code's way out) — `…WindowWait`,
 * `…Window`, `…Wait`, then the bare row (LIMIT-07) — then the generic
 * `errors.limit.<unit>` (`…Current` when the server measured the user's value,
 * `…Wait` when the wait is known), which also ends with a way out. undefined ⇒
 * the caller keeps resolving. The numbers always come from the server.
 */
function limitMessage(code: ErrorCode | number, values: Record<string, string | number>): string | undefined {
  const hasWait = typeof values.retryAfter === 'string';
  const hasWindow = typeof values.window === 'string';
  const name = codeName(code);
  if (name) {
    const suffixes = [
      ...(hasWindow && hasWait ? ['WindowWait'] : []),
      ...(hasWindow ? ['Window'] : []),
      ...(hasWait ? ['Wait'] : []),
      '',
    ];
    for (const s of suffixes) {
      const perCode = translateError(`limit.${name}${s}`, values);
      if (perCode) return perCode;
    }
  }
  const unit = values.unit;
  if (!isLimitUnitName(unit)) return undefined;
  const base = `limit.${unit}${typeof values.current === 'number' && values.current > 0 ? 'Current' : ''}`;
  if (hasWait) {
    const withWait = translateError(`${base}Wait`, values);
    if (withWait) return withWait;
  }
  return translateError(base, values);
}

/**
 * friendlyMessage resolves the warm message for a typed code, localized
 * through the errors.* catalog (R8 Phase 1b) via the imperative bridge.
 *
 * Resolution order:
 *  1. The demo read-only refusal → its own copy.
 *  2. RESEARCH_CONCURRENCY_LIMIT with a numeric `values.limit` →
 *     t(`errors.research.concurrencyLimit`, values), which names the plan's
 *     limit (BILL-10); when that key does not resolve, the steps below.
 *  3. A limit (`values.limit` > 0, LIMIT-03): a `serverMessage` that names
 *     the number as a whole token (or a RATE_LIMITED / PAYLOAD_TOO_LARGE
 *     detail naming a number, prefersLimitDetail) → the server detail; else
 *     the per-code `errors.limit.<NAME>[WindowWait|Window|Wait]` key (the
 *     most specific whose window and wait are known); else, for a known
 *     unit, the generic `errors.limit.<unit>[Current][Wait]` row. Every such
 *     row ends with a way out. When none resolves, the steps below.
 *  4. VALIDATION/CONFLICT with a non-empty `serverMessage` → the server
 *     detail (see DETAIL_PREFERRING).
 *  5. RATE_LIMITED with a `serverMessage` naming a concrete wait, or
 *     PAYLOAD_TOO_LARGE with one naming the limit → the server detail
 *     (prefersLimitDetail).
 *  5b. RATE_LIMITED with a wait that no row above resolved (no limit, or a
 *     limit no row matched) → `errors.wait.RATE_LIMITED` (LIMIT-07), which
 *     names the wait.
 *  6. Known code → t(`errors.<NAME>`), falling back to the English literal
 *     in CATALOG when the bridge / key is unavailable (pre-hydration, older
 *     catalog).
 *  7. Unknown code with a non-empty `serverMessage` → the server message
 *     (always safe — the backend guarantees it; better than a generic
 *     fallback for newer-backend/older-frontend pairings).
 *  8. GENERIC_FALLBACK, localized when possible.
 *
 * `values` are ICU arguments for codes with an interpolated variant; every
 * limit code reads `limit`, `unit`, `current`; a rate limit also `window` and
 * `retryAfter` (build them with limitValues).
 */
export function friendlyMessage(
  code: ErrorCode | number | undefined | null,
  serverMessage?: string,
  values?: Record<string, string | number>,
): string {
  const trimmed = serverMessage?.trim();
  // The demo refusal is resolved first: over HTTP its machine code arrives IN
  // the message slot (the gate answers `{"error":"demo_read_only"}`), which the
  // detail-preferring branch below would otherwise echo verbatim; over WS it
  // arrives as code 220, which no catalog entry covers.
  if (isDemoReadOnlyRefusal({ code: code ?? undefined, message: trimmed })) {
    return translateError('DEMO_READ_ONLY') ?? DEMO_READ_ONLY_ENTRY.message;
  }
  if (code === ErrorCode.RESEARCH_CONCURRENCY_LIMIT && typeof values?.limit === 'number') {
    const named = translateError('research.concurrencyLimit', values);
    if (named) return named;
  }
  if (code != null && typeof values?.limit === 'number' && values.limit > 0) {
    // A detail that names the number is the richer text: it names WHAT hit the limit
    // and the way out (contexts, expertise). It stays English, like every
    // detail-preferring detail; a RATE_LIMITED detail that names a wait wins too.
    // A limit >= 1000 renders grouped in its own detail ("1,000" en, "1.000" de,
    // "1 000" fr with U+202F or U+00A0), so the digit groups are joined before
    // the match (LIMIT-37).
    const ungrouped = trimmed?.replace(/(\d)[,.   ](?=\d{3}(\D|$))/g, '$1');
    if (
      trimmed &&
      (new RegExp(`(^|\\D)${values.limit}(\\D|$)`).test(ungrouped ?? trimmed) || prefersLimitDetail(code, trimmed))
    ) {
      return trimmed;
    }
    const named = limitMessage(code, values);
    if (named) return named;
  }
  if (code != null && DETAIL_PREFERRING.has(code as ErrorCode) && trimmed) {
    return trimmed;
  }
  if (code != null && trimmed && prefersLimitDetail(code, trimmed)) {
    return trimmed;
  }
  if (code === ErrorCode.RATE_LIMITED && typeof values?.retryAfter === 'string') {
    const wait = translateError('wait.RATE_LIMITED', values);
    if (wait) return wait;
  }
  const known = code != null && code !== ErrorCode.UNSPECIFIED && CATALOG[code as ErrorCode];
  if (known) {
    const name = codeName(code as number);
    return (name && translateError(name)) ?? known.message;
  }
  if (trimmed) return trimmed;
  return translateError('GENERIC_FALLBACK') ?? GENERIC_FALLBACK.message;
}

/** isRetryable reports whether the UI should offer a "Try again" affordance. */
export function isRetryable(code: ErrorCode | number | undefined | null): boolean {
  return lookup(code).retryable;
}
